import { useFormik } from 'formik';
import { initialValues, validationSchema } from './formData';
import { ISelect, ParamEnum } from '../../../../../../shared/types/shared';
import { useEffect, useState } from 'react';
import { PqrDataSfc } from '../../../../../../shared/types/modules/pqr';
import { PqrDataSfcProps } from './props';
import {
    asyncGetParamById,
    asyncGetParams,
} from '../../../../../../services/param.service';
import {
    asyncGetCities,
    asyncGetCountries,
    asyncGetCountry,
} from '../../../../../../services/location.service';
import dayjs from 'dayjs';

export const usePqrDataSfc = ({
    data,
    onChangeData,
    onChangeFormik,
    receptionChannel,
    country,
    location,
    hasReconsideration,
    status,
    half,
    inFavorOf,
    type,
    isClousureStatus,
    submotive1,
    submotive2,
    responseDate,
    customerFilingDatePqrStatus,
    isCausalityFraud,
}: PqrDataSfcProps) => {
    const sfcTypes = [`${ParamEnum.PQR_TYPE}_QOR`, `${ParamEnum.PQR_TYPE}_QEX`];
    const [channelPresentsFailures, setChannelPresentsFailures] = useState<
        ISelect[]
    >([]);
    const [controlEntities, setControlEntities] = useState<ISelect[]>([]);
    const [receptionInstances, setReceptionInstances] = useState<ISelect[]>([]);
    const [desistItems, setDesistItems] = useState<ISelect[]>([]);
    const [receptionPoints, setReceptionPoints] = useState<ISelect[]>([]);
    const [countries, setCountries] = useState<ISelect[]>([]);
    const [locations, setLocations] = useState<ISelect[]>([]);
    const [statusParam, setStatusParam] = useState<ISelect>();
    const [typeParam, setTypeParam] = useState<ISelect>();
    const [inFavorOfParam, setInFavorOfParam] = useState<ISelect>();
    const [markingItems, setMarkingItems] = useState<ISelect[]>([]);
    const [admissions, setAdmissions] = useState<ISelect[]>([]);
    const [fraudTypes, setFraudTypes] = useState<ISelect[]>([]);
    const [fraudModes, setFraudModes] = useState<ISelect[]>([]);
    const [isDcf, setIsDcf] = useState<boolean>(false);
    const [isRequiredLocation, setIsRequiredLocation] =
        useState<boolean>(false);
    const [acceptances, setAcceptances] = useState<ISelect[]>([]);
    const [rectifications, setRectifications] = useState<ISelect[]>([]);
    const [initialStatus, setInitialStatus] = useState<string | undefined>(
        undefined
    );
    const [initialStatusParam, setInitialStatusParam] = useState<ISelect>();
    const [isRequest, setIsRequest] = useState<boolean>(false);
    const [isSfc, setIsSfc] = useState<boolean>(false);
    const [isFraud, setIsFraud] = useState(false);

    const onSubmit = (data: PqrDataSfc) => {
        if (onChangeData) onChangeData(data || {});
    };

    const isExtension = statusParam?.code === `${ParamEnum.PQR_STATUS}_PRO`;
    const isRequiredFraudParams =
        statusParam?.code !== `${ParamEnum.PQR_STATUS}_ABI` &&
        statusParam?.code !== `${ParamEnum.PQR_STATUS}_ANU` &&
        isFraud;
    const isInFavorOf =
        (inFavorOfParam?.code === `${ParamEnum.PQR_IN_FAVOR_OF_SMART}_FVBL` ||
            inFavorOfParam?.code ===
                `${ParamEnum.PQR_IN_FAVOR_OF_SMART}_PFVBL`) &&
        isClousureStatus &&
        isDcf;
    const isRectification = Boolean(isDcf && isClousureStatus);
    const isClosedStatus = statusParam?.code === `${ParamEnum.PQR_STATUS}_CER`;
    const isSolvedStatus = statusParam?.code === `${ParamEnum.PQR_STATUS}_SOL`;
    const isDisabledClosingDateDCF = !(
        isClosedStatus &&
        isDcf &&
        initialStatusParam?.code === `${ParamEnum.PQR_STATUS}_SOL`
    );

    const formik = useFormik({
        initialValues,
        validationSchema: validationSchema(
            isRectification,
            isInFavorOf,
            isDcf,
            isRequiredLocation,
            isRequiredFraudParams,
            responseDate as Date
        ),
        onSubmit,
    });

    useEffect(() => {
        if (onChangeFormik) onChangeFormik(formik);
    }, [formik.values, formik.errors]);

    useEffect(() => {
        formik?.setFieldValue(
            'channelPresentsFailure',
            data?.channelPresentsFailure || ''
        );
        formik?.setFieldValue('controlEntity', data?.controlEntity || '');
        formik?.setFieldValue(
            'receptionInstance',
            isRequest && isSfc
                ? receptionInstances.filter(
                    (item) =>
                        item.code ===
                          `${ParamEnum.PQR_RECEPTION_INSTANCE}_SFC`
                )[0]?.id || ''
                : data?.receptionInstance || ''
        );
        formik?.setFieldValue('desistd', data?.desistd || '');
        formik?.setFieldValue('receptionPoint', data?.receptionPoint || '');
        formik?.setFieldValue('admission', data?.admission || '');
        formik?.setFieldValue('acceptance', data?.acceptance || '');
        formik?.setFieldValue('rectification', data?.rectification || '');
        formik?.setFieldValue(
            'hasExpressComplaint',
            data?.hasExpressComplaint || ''
        );
        formik?.setFieldValue(
            'hasReconsideration',
            Boolean(hasReconsideration)
        );
        formik?.setFieldValue(
            'isVirtualProduct',
            Boolean(data?.isVirtualProduct)
        );
        formik?.setFieldValue('isGuardianship', Boolean(data?.isGuardianship));
        formik?.setFieldValue('guardianship', data?.guardianship || '');
        formik?.setFieldValue('dcfScaling', Boolean(data?.dcfScaling) ?? false);
        formik?.setFieldValue('marking', data?.marking || '');
        formik?.setFieldValue('updateStatusPQRDate', data?.updateStatusPQRDate);
        formik?.setFieldValue(
            'answerSFCDate',
            data?.answerSFCDate ? dayjs(data?.answerSFCDate) : undefined
        );

        formik?.setFieldValue(
            'closingDateDCF',
            data?.closingDateDCF ? dayjs(data?.closingDateDCF) : undefined
        );
        formik?.setFieldValue(
            'customerFilingDate',
            data?.customerFilingDate
                ? dayjs(data?.customerFilingDate)
                : undefined
        );
        formik?.setFieldValue('detailProduct', data?.detailProduct || '');

        formik?.setFieldValue(
            'finalResponseAttached',
            Boolean(data?.finalResponseAttached)
        );
        formik?.setFieldValue('fraudType', data?.fraudType || '');
        formik?.setFieldValue('fraudMode', data?.fraudMode || '');
        formik?.setFieldValue('claimedAmount', data?.claimedAmount || '');
        formik?.setFieldValue('approvedAmount', data?.approvedAmount || '');
    }, [data, isRequest, isSfc]);

    useEffect(() => {
        formik?.setFieldValue('country', country || '');
    }, [country]);

    useEffect(() => {
        formik?.setFieldValue('location', location || '');
    }, [location]);

    useEffect(() => {
        asyncGetParams(ParamEnum.PQR_CHANNEL_PRESENTS_FAILURE).then((items) =>
            setChannelPresentsFailures(items)
        );
        asyncGetParams(ParamEnum.PQR_CONTROL_ENTITY).then((items) =>
            setControlEntities(items)
        );
        asyncGetParams(ParamEnum.PQR_RECEPTION_INSTANCE).then((items) =>
            setReceptionInstances(items)
        );
        asyncGetParams(ParamEnum.PQR_DESISTD).then((items) =>
            setDesistItems(items)
        );
        asyncGetCountries().then((items) => setCountries(items));
        asyncGetCities().then((items) => setLocations(items));
        asyncGetParams(ParamEnum.PQR_RECTIFICATION).then((items) =>
            setRectifications(items)
        );
        asyncGetParams(ParamEnum.PQR_FRAUD_TYPE).then((items) =>
            setFraudTypes(items)
        );
        asyncGetParams(ParamEnum.PQR_FRAUD_MODE).then((items) =>
            setFraudModes(items)
        );
    }, []);

    useEffect(() => {
        if (inFavorOf)
            asyncGetParams(ParamEnum.PQR_ACCEPTANCE, undefined, inFavorOf).then(
                (items) => setAcceptances(items)
            );
        else setAcceptances([]);
    }, [inFavorOf]);

    useEffect(() => {
        if (
            acceptances
                .map((item) => item?.id)
                .includes(data?.acceptance as string)
        ) {
            formik?.setFieldValue('acceptance', data?.acceptance || '');
        } else formik?.setFieldValue('acceptance', '');
    }, [acceptances]);

    useEffect(() => {
        if (Boolean(receptionChannel?.trim()))
            asyncGetParams(
                ParamEnum.PQR_RECEPTION_POINT,
                undefined,
                receptionChannel
            ).then((items) => {
                setReceptionPoints(items);
                formik?.setFieldValue('receptionPoint', items?.[0]?.id || '');
            });
        else {
            setReceptionPoints([]);
            formik?.setFieldValue('receptionPoint', '');
        }
    }, [receptionChannel]);

    useEffect(() => {
        if (initialStatus === undefined && status) {
            setInitialStatus(status);
        }
    }, [status]);

    useEffect(() => {
        if (type !== undefined) {
            asyncGetParamById(type).then((item) => {
                setTypeParam(item);
            });
        }
    }, [type]);

    useEffect(() => {
        if (status) {
            asyncGetParamById(status).then((item) => {
                setStatusParam(item);
                if (
                    item?.code === `${ParamEnum.PQR_STATUS}_CER` &&
                    status !== initialStatus
                ) {
                    formik?.setFieldValue('marking', '');
                } else {
                    formik?.setFieldValue('marking', data?.marking || '');
                }
            });
        }
    }, [status, initialStatus, data]);

    useEffect(() => {
        if (inFavorOf)
            asyncGetParamById(inFavorOf).then((items) =>
                setInFavorOfParam(items)
            );
    }, [inFavorOf]);

    useEffect(() => {
        formik?.setFieldValue('isExtension', isExtension);
    }, [isExtension]);

    const filterParamsByHalf = (items: ISelect[], half?: string) => {
        return (
            items?.filter((param: ISelect) => param?.parent?.id === half) || []
        );
    };

    const asyncNotApplicableParam = (items: ISelect[]) => {
        return (
            items?.filter(
                (param: ISelect) =>
                    param?.code === `${ParamEnum.PQR_ADMISSION}_005`
            ) || []
        );
    };

    useEffect(() => {
        if (Boolean(half?.trim())) {
            asyncGetParams(ParamEnum.PQR_ADMISSION).then((items) => {
                const filteredParams = filterParamsByHalf(items, half);
                if (filteredParams.length > 0) {
                    setAdmissions(filteredParams);
                    setIsDcf(true);
                } else {
                    const notApplicableParam = asyncNotApplicableParam(items);
                    setAdmissions(notApplicableParam);
                    setIsDcf(false);
                    formik?.setFieldValue(
                        'admission',
                        notApplicableParam[0]?.id || ''
                    );
                }
            });

            asyncGetParamById(half || '').then((item) => {
                setIsSfc(item?.code === `${ParamEnum.PQR_HALF}_003`);
            });
        }
    }, [half]);

    useEffect(() => {
        if (Boolean(type?.trim())) {
            asyncGetParamById(type).then((item) => {
                setIsRequest(item?.code === `${ParamEnum.PQR_TYPE}_PTC`);
                if (item?.code === `${ParamEnum.PQR_TYPE}_QEX`) {
                    formik?.setFieldValue('hasExpressComplaint', true);
                }
            });
        }
    }, [type]);

    useEffect(() => {
        if (statusParam?.valueSmart)
            formik?.setFieldValue('status', `KEY_${statusParam?.valueSmart}`);

        const isSfcType = typeParam?.code && sfcTypes.includes(typeParam?.code);

        if (statusParam?.code === `${ParamEnum.PQR_STATUS}_CER`) {
            const codeNA = `${ParamEnum.PQR_MARKING}_N/A`;
            asyncGetParams(ParamEnum.PQR_MARKING).then((items) => {
                setMarkingItems(
                    isSfcType
                        ? items
                        : items.filter(
                            (item) => !isSfcType && item?.code === codeNA
                        )
                );
            });
        } else {
            const codeNA = `${ParamEnum.PQR_MARKING_PROCEDURE}_005`;
            asyncGetParams(ParamEnum.PQR_MARKING_PROCEDURE).then((items) =>
                setMarkingItems(
                    items.filter(
                        (item) =>
                            (isSfcType && item?.code !== codeNA) ||
                            (!isSfcType && item?.code === codeNA)
                    )
                )
            );
        }
    }, [statusParam, typeParam]);

    useEffect(() => {
        if (formik.values.country)
            asyncGetCountry(formik.values.country).then((item) => {
                setIsRequiredLocation(false);
                if (item?.code === '170') {
                    setIsRequiredLocation(true);
                }
            });
    }, [formik.values.country]);

    useEffect(() => {
        const newIsSfcType =
            typeParam?.code && sfcTypes.includes(typeParam?.code);

        const isFraud = (newIsSfcType as boolean) && isCausalityFraud === true;
        setIsFraud(isFraud);
        if (!isFraud) {
            formik?.setFieldValue('fraudMode', null);
            formik?.setFieldValue('fraudType', null);
            formik?.setFieldValue('approvedAmount', null);
            formik?.setFieldValue('claimedAmount', null);
        } else {
            formik?.setFieldValue('fraudMode', data?.fraudMode);
            formik?.setFieldValue('fraudType', data?.fraudType);
            formik?.setFieldValue('approvedAmount', data?.approvedAmount);
            formik?.setFieldValue('claimedAmount', data?.claimedAmount);
        }
    }, [typeParam, submotive1, submotive2, data, isCausalityFraud]);

    useEffect(() => {
        if (initialStatus !== undefined && initialStatus !== status) {
            formik?.setFieldValue('updateStatusPQRDate', dayjs());
        } else {
            formik?.setFieldValue(
                'updateStatusPQRDate',
                data?.updateStatusPQRDate
            );
        }
    }, [status]);

    useEffect(() => {
        if (isSolvedStatus && !formik?.values?.customerFilingDate) {
            formik?.setFieldValue(
                'customerFilingDate',
                customerFilingDatePqrStatus
            );
        }
    }, [statusParam]);

    useEffect(() => {
        const fetchParams = async () => {
            if (!half) return;

            let initialStatusResponse = null;
            if (initialStatus) {
                initialStatusResponse = await asyncGetParamById(initialStatus);
                setInitialStatusParam(initialStatusResponse);
            }

            if (
                isDcf &&
                initialStatusResponse?.code === `${ParamEnum.PQR_STATUS}_SOL` &&
                statusParam?.code === `${ParamEnum.PQR_STATUS}_CER`
            ) {
                formik.setFieldValue(
                    'answerSFCDate',
                    formik.values.closingDateDCF
                );
            }
        };

        fetchParams();
    }, [formik.values.closingDateDCF, half, initialStatus, status]);

    return {
        formik,
        channelPresentsFailures,
        controlEntities,
        desistItems,
        receptionInstances,
        receptionPoints,
        countries,
        locations,
        statusParam,
        markingItems,
        admissions,
        isDcf,
        acceptances,
        rectifications,
        isRectification,
        isInFavorOf,
        isRequest,
        isSfc,
        isRequiredLocation,
        fraudTypes,
        fraudModes,
        isFraud,
        isRequiredFraudParams,
        isDisabledClosingDateDCF,
        responseDate,
    };
};
